import { Badge, Banner, Button, Dialog, Field, Input } from '@cloudflare/kumo';
import { useCallback, useEffect, useState } from 'react';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';
import { CAPI_EVENT_LABELS } from './flow-nodes/MetaConversionConfig';

interface CapiEvent { id: string; eventName: string; value: string | null; status: string; source: string; error: string | null; createdAt: string }
interface Status { enabled: boolean; datasetId: string | null; testEventCode: string; adLeads7d: number; recent: CapiEvent[] }
const STATUS: Record<string, { label: string; color: 'green' | 'orange' | 'red' | 'neutral' }> = {
  SENT: { label: 'Enviado', color: 'green' }, PENDING: { label: 'Tentando', color: 'orange' }, FAILED: { label: 'Falhou', color: 'red' }, SKIPPED: { label: 'Ignorado', color: 'neutral' },
};
const SOURCE: Record<string, string> = { FLOW: 'fluxo', CAPTURE: 'captação', API: 'API' };

/** API oficial › Conversões (Conversions API do Meta Ads) de uma conexão */
export function MetaCapiModal({ sessionName, label, canManage, onClose }: { sessionName: string; label: string; canManage: boolean; onClose: () => void }) {
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [st, setSt] = useState<Status | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const url = `/api/tools/api-oficial/${encodeURIComponent(sessionName)}/capi`;

  const load = useCallback(async () => {
    const r = await fetchApi(url);
    if (r.ok) { const d = await r.json(); setSt(d); setCode(d.testEventCode || ''); }
  }, [fetchApi, url]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    const r = await fetchApi(url, { method: 'POST', body: JSON.stringify({ testEventCode: code }) });
    const j = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) { toast.error(j.message || 'Não foi possível ativar'); return; }
    toast.success(st?.enabled ? 'Código de teste salvo' : 'Conversões ativadas');
    load();
  };

  return (
    <Dialog.Root open={true} onOpenChange={(o: boolean) => { if (!o) (onClose)(); }}>
<Dialog size="lg" className="p-6">


      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Conversões do Meta Ads · {label}</h3>
        <div style={{ color: 'var(--text-muted)' }}>
          Quem chega por anúncio "clique para WhatsApp" é marcado no contato. Quando ele se cadastra na captação, passa pelo bloco
          "Conversão Meta Ads" do fluxo ou o seu sistema avisa pela API (<code>POST /api/v1/conversions</code>), o Meta recebe a
          conversão e otimiza o anúncio para quem converte.
        </div>
        {st && (
          <>
            <div className="flex items-center gap-3">
              {st.enabled ? <Badge variant="green">Ativo</Badge> : <Badge variant="neutral">Desativado</Badge>}
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{st.adLeads7d} contato{st.adLeads7d === 1 ? '' : 's'} vindos de anúncio nos últimos 7 dias{st.datasetId ? ` · dataset ${st.datasetId}` : ''}</div>
            </div>
            <div className="flex items-end gap-3">
              <div style={{ flex: 1, maxWidth: 320 }}>
                <Field label="Código de teste (opcional)" description="Do Gerenciador de Eventos, para ver os eventos em Testar eventos">
                  <Input onChange={(e) => setCode(e.target.value)} value={code} disabled={!canManage} />
                </Field>
              </div>
              {canManage && <Button variant="primary" loading={busy} onClick={save}>{st.enabled ? 'Salvar' : 'Ativar conversões'}</Button>}
            </div>
            {st.recent.length === 0 && <Banner variant="default" description={<>Nenhum evento enviado ainda.</>} />}
            {st.recent.map((e) => {
              const s = STATUS[e.status] ?? { label: e.status, color: 'neutral' as const };
              return (
                <div key={e.id} className="flex items-center justify-between gap-3" style={{ borderTop: '1px solid var(--border)', paddingTop: 8, fontSize: 13 }}>
                  <span>{CAPI_EVENT_LABELS[e.eventName] ?? e.eventName}{e.value ? ` · R$ ${Number(e.value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : ''} · {SOURCE[e.source] ?? e.source} · {new Date(e.createdAt).toLocaleString('pt-BR')}</span>
                  <span className="flex items-center gap-2">{e.error && <small style={{ color: 'var(--crm-text-error)' }}>{e.error}</small>}<Badge variant={s.color}>{s.label}</Badge></span>
                </div>
              );
            })}
          </>
        )}
        <div className="flex justify-end"><Button variant="secondary" onClick={onClose}>Fechar</Button></div>
      </div>
    
</Dialog>
</Dialog.Root>
  );
}
